import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UploadCloud, Loader2, Download, X, AlertTriangle, FileText, Check,
  ZoomIn, ZoomOut, Maximize2, Terminal, Save, RefreshCw, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fetchAuthedImage, useAuthedImage } from "@/utils/authedImage";

// v3 runs 30-60s; the job is polled, so no single request ever waits that long.
const POLL_MS = 5000;
// Must outlast the server's own safety limit, or the UI gives up on a job that is
// still running and the user sees a false failure.
const POLL_TIMEOUT_MS = 16 * 60 * 1000;

// Every column is a field the engine already parsed — nothing is re-derived here. In
// particular Value is rendered verbatim: "143 0/-1" is a nominal of 143 with a 0/-1
// tolerance, and stripping its spaces turned it into 1430.
const COLUMNS = [
  { key: "No", label: "#", w: "w-12" },
  { key: "View", label: "View" },
  { key: "Value", label: "Value", mono: true, verbatim: true },
  { key: "Type", label: "Type", fallback: "Feature" },
  { key: "Nominal", label: "Nom." },
  { key: "TolPlus", label: "Tol+" },
  { key: "TolMinus", label: "Tol−" },
  { key: "Lower", label: "Lower" },
  { key: "Upper", label: "Upper" },
  { key: "Unit", label: "Unit" },
  { key: "Count", label: "Qty" },
  { key: "Reference", label: "Ref" },
  { key: "Basic", label: "Basic", basic: true },
  { key: "GDT", label: "GD&T" },
  { key: "Datums", label: "Datums" },
  { key: "Label", label: "Label" },
  { key: "Confidence", label: "Conf." },
  { key: "x_mm", label: "x mm" },
  { key: "y_mm", label: "y mm" },
];

const ENGINE_ORDER = ["auto", "bs", "v3", "legacy"];
// The manual engine picker is hidden: every run goes through "auto", which already
// routes a CAD PDF to the v3 text-layer path and a scan/image to Balloon Studio. The
// engines themselves are UNCHANGED — this only takes the buttons off the screen, so
// flipping this back to true restores the picker with no other edits.
const SHOW_ENGINE_PICKER = false;
const ENGINE_SHORT = {
  auto: "Auto",
  bs: "Balloon Studio",
  bs_ocr: "Balloon Studio (OCR)",
  v3: "v3 legacy (scan)",
  legacy: "Legacy hybrid",
};

// Queue position → "1st / 2nd / 3rd / Nth".
function ordinal(n) {
  if (!n) return "";
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return Number.isInteger(v) ? v : v.toFixed(2);
  if (typeof v === "boolean") return v ? "Y" : "N";
  return String(v);
}

/** Zoomable, pannable image. Used for the ballooned drawing and the CV overlay.
 *
 * A ballooned A3 scan is ~5000 px wide, so 1:1 shows a corner of the sheet. The view
 * therefore opens at "fit", and that fit is the zoom-out floor — the percentage shown is
 * relative to the image's natural pixels, so 100% means one screen pixel per scan pixel.
 * Zooming keeps the point under the cursor fixed, which is what makes stepping in on a
 * single dimension feel direct rather than drifting toward a corner.
 */
const ZOOM_MAX = 24;      // deep enough to inspect a single OCR'd glyph
const ZOOM_STEP = 1.6;

function ZoomableImage({ src, alt, highlight }) {
  const wrapRef = useRef(null);
  const [nat, setNat] = useState(null);        // natural image size
  const [fit, setFit] = useState(1);           // scale that fits the whole sheet
  const [scale, setScale] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const measure = useCallback((natural) => {
    const el = wrapRef.current;
    if (!el || !natural) return;
    const bw = el.clientWidth, bh = el.clientHeight;
    const f = Math.min(bw / natural.w, bh / natural.h);
    setFit(f);
    setScale(f);
    setOff({ x: (bw - natural.w * f) / 2, y: (bh - natural.h * f) / 2 });
  }, []);

  const onLoad = (e) => {
    const natural = { w: e.target.naturalWidth, h: e.target.naturalHeight };
    setNat(natural);
    measure(natural);
  };

  // Re-fit when the panel is resized, so the floor stays correct.
  useEffect(() => {
    if (!nat || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measure(nat));
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [nat, measure]);

  useEffect(() => { setNat(null); }, [src]);

  /** Scale to `next`, keeping image-point under (px,py) — container coords — in place. */
  const zoomAt = useCallback((next, px, py) => {
    setScale((s) => {
      const t = Math.max(fit, Math.min(ZOOM_MAX, next));
      setOff((o) => ({ x: px - (px - o.x) * (t / s), y: py - (py - o.y) * (t / s) }));
      return t;
    });
  }, [fit]);

  const zoomCentre = (factor) => {
    const el = wrapRef.current;
    if (!el) return;
    zoomAt(scale * factor, el.clientWidth / 2, el.clientHeight / 2);
  };

  // Native listener: React's onWheel is passive, so preventDefault there is ignored and
  // the page scrolls instead of the image zooming.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const handler = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const factor = Math.pow(1.0022, -e.deltaY);   // smooth, trackpad-friendly
      zoomAt(scale * factor, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [scale, zoomAt]);

  const onDown = (e) => { drag.current = { x: e.clientX - off.x, y: e.clientY - off.y }; };
  const onMove = (e) => {
    if (!drag.current) return;
    setOff({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const onUp = () => { drag.current = null; };
  const onDouble = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    zoomAt(scale * 2.5, e.clientX - r.left, e.clientY - r.top);
  };

  if (!src) {
    return (
      <div className="flex h-72 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        This run produced no image.
      </div>
    );
  }

  const pct = Math.round(scale * 100);
  const atFit = Math.abs(scale - fit) < 0.001;
  return (
    <div className="relative">
      <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md border bg-background/95 p-1 shadow-sm">
        <button className="rounded p-1 hover:bg-muted disabled:opacity-30" disabled={scale >= ZOOM_MAX}
                onClick={() => zoomCentre(ZOOM_STEP)} title="Zoom in">
          <ZoomIn className="h-4 w-4" />
        </button>
        <button className="rounded p-1 hover:bg-muted disabled:opacity-30" disabled={atFit}
                onClick={() => zoomCentre(1 / ZOOM_STEP)} title="Zoom out">
          <ZoomOut className="h-4 w-4" />
        </button>
        <button className="rounded px-1.5 py-1 text-xs hover:bg-muted disabled:opacity-30" disabled={atFit}
                onClick={() => nat && measure(nat)} title="Fit whole sheet">
          <Maximize2 className="h-4 w-4" />
        </button>
        <button className="rounded px-1.5 py-1 text-xs hover:bg-muted"
                onClick={() => { const el = wrapRef.current; zoomAt(1, el.clientWidth / 2, el.clientHeight / 2); }}
                title="Actual size (1 screen pixel per scan pixel)">
          1:1
        </button>
        <span className="w-12 text-center text-xs tabular-nums text-muted-foreground">{pct}%</span>
      </div>
      <div
        ref={wrapRef}
        className="h-[72vh] min-h-[420px] cursor-grab overflow-hidden rounded-md border bg-white active:cursor-grabbing"
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onDoubleClick={onDouble}
      >
        <img
          src={src} alt={alt} draggable={false} onLoad={onLoad}
          style={{
            transform: `translate(${off.x}px, ${off.y}px) scale(${scale})`,
            transformOrigin: "top left",
            imageRendering: scale > 2 ? "pixelated" : "auto",
          }}
          className="max-w-none select-none"
        />
      </div>
      {highlight != null && (
        <p className="mt-1 text-xs text-muted-foreground">
          Row <strong>#{highlight}</strong> selected — its balloon carries the same number on the drawing.
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        Scroll to zoom · drag to pan · double-click to zoom in · <strong>Fit</strong> for the whole sheet · <strong>1:1</strong> for actual pixels
      </p>
    </div>
  );
}

export default function KcTest() {
  const navigate = useNavigate();
  const [engine, setEngine] = useState("auto");
  const [status, setStatus] = useState(null);
  const [kcRecents, setKcRecents] = useState([]);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("ballooned");
  const [edits, setEdits] = useState({});
  const [savingEdits, setSavingEdits] = useState(false);
  const [showLog, setShowLog] = useState(false);
  // Every engine's finished result, kept so the same drawing can be compared side by side.
  const [compare, setCompare] = useState({});
  // Set when a run ended without a result the page could show — drives the retry banner.
  const [lostJob, setLostJob] = useState(null);
  // Set when a result was found again after its own job id stopped resolving.
  const [recovered, setRecovered] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  // KC Recents — finished KC extractions (source="kc" ballooning docs), newest first.
  const loadRecents = useCallback(async () => {
    try {
      const { data } = await api.get("/ballooning", { params: { source: "kc" } });
      setKcRecents((data.documents || []).filter((d) => d.kc_status === "done").slice(0, 8));
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadRecents(); }, [loadRecents]);

  // Remove a run from Recents — the same soft delete the Ballooning list uses, which
  // only stamps deleted_at. Nothing is shredded, but the Trash PAGE covers NPD and
  // Projects only, so there is no self-service restore for a KC run: the wording below
  // says "hidden", not "recoverable", because the user cannot undo this themselves.
  const [deletingId, setDeletingId] = useState(null);
  const deleteRecent = useCallback(async (d) => {
    if (!window.confirm(`Remove "${d.name}" from KC Recents?\n\nThe extraction is hidden from the list. There is no undo from the app — re-run the drawing if you need it back.`)) return;
    setDeletingId(d.id);
    try {
      await api.delete(`/ballooning/${d.id}`);
      setKcRecents((list) => list.filter((x) => x.id !== d.id));
      toast.success("Removed from KC Recents.");
      loadRecents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete this run.");
    } finally {
      setDeletingId(null);
    }
  }, [loadRecents]);

  useEffect(() => {
    api.get("/kc/status")
      .then((r) => {
        setStatus(r.data);
        if (!r.data?.v3?.available && !r.data?.bs?.available) setEngine("legacy");
      })
      .catch(() => setStatus(null));
    return () => clearInterval(pollRef.current);
  }, []);

  const rows = job?.status === "done" ? job.rows || [] : [];
  const review = job?.status === "done" ? job.review || [] : [];
  const warnings = job?.status === "done" ? job.warnings || [] : [];
  const v3Ready = !!status?.v3?.available;
  const bsReady = !!status?.bs?.available;
  const engineReady = (e) => (e.startsWith("bs") ? bsReady : e === "v3" ? v3Ready : true);
  const accept = `.${(status?.allowed_extensions || ["pdf", "png", "jpg", "jpeg", "tif", "tiff", "bmp"]).join(",.")}`;

  const pickFile = (f) => {
    if (!f) return;
    // Decide by EXTENSION, never by MIME: Windows browsers send "" or
    // application/octet-stream for .tif, and this list is the server's own.
    const exts = status?.allowed_extensions || ["pdf", "png", "jpg", "jpeg", "tif", "tiff", "bmp"];
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!exts.includes(ext)) {
      return toast.error(`Unsupported file type ".${ext}" — upload ${exts.map((e) => `.${e}`).join(" ")}`);
    }
    setFile(f);
    setJob(null);
    setSelected(null);
    setEdits({});
  };

  // Adopt a finished payload: fill the table, the comparison column and the toast.
  const adopt = useCallback((data) => {
    setJob(data);
    const ran = data.engine_used || data.engine;
    setCompare((c) => ({ ...c, [ran]: data }));
    toast.success(
      `${ENGINE_SHORT[ran] || ran} → ${(data.rows || []).length} row(s) in ${data.elapsed_seconds}s`
    );
  }, []);

  /* A job id can stop resolving (the run was interrupted and took its id with it). The
     upload is still in the picker, so ask the server for the most recent run of THIS
     file — name + size, last hour — and open its result instead of losing it. */
  const recover = useCallback(async (f) => {
    if (!f) return false;
    try {
      const { data } = await api.get("/kc/recover", {
        params: { filename: f.name, size: f.size },
      });
      if (!data?.found || data.status !== "done") return false;
      const { data: full } = await api.get(`/kc/jobs/${data.job_id}`);
      if (full?.status !== "done") return false;
      setRecovered({ job_id: data.job_id, doc_id: data.doc_id });
      adopt(full);
      toast.success("Recovered the finished result of this drawing — nothing was lost.");
      return true;
    } catch {
      return false;
    }
  }, [adopt]);

  const poll = useCallback((jobId, uploaded) => {
    const started = Date.now();
    let misses = 0;   // consecutive 404s — tolerate the brief window after a server restart
    clearInterval(pollRef.current);
    setLostJob(null);
    pollRef.current = setInterval(async () => {
      if (Date.now() - started > POLL_TIMEOUT_MS) {
        clearInterval(pollRef.current);
        setBusy(false);
        return toast.error("Gave up waiting — the job is still running on the server.");
      }
      try {
        const { data } = await api.get(`/kc/jobs/${jobId}`);
        misses = 0;
        setJob(data);
        if (data.status === "done" || data.status === "failed") {
          clearInterval(pollRef.current);
          setBusy(false);
          if (data.status === "failed") {
            toast.error(`Extraction failed: ${data.error || "unknown error"}`);
            setLostJob({ reason: data.error || "The extraction failed." });
          } else {
            // Key by the engine that actually RAN, not the one requested — "auto" would
            // otherwise overwrite itself and the comparison table could never fill.
            adopt(data);
            loadRecents();
            // Open the full KcViewer result screen automatically once the drawing is done.
            if (data.result_doc_id) navigate(`/kc-test/${data.result_doc_id}`);
          }
        }
      } catch (e) {
        const status = e?.response?.status;
        // A 404 right after a restart can be transient; give it a few ticks, then stop
        // guessing and go looking for the result by what the user still has on screen.
        if (status === 404) {
          misses += 1;
          if (misses < 4) return;   // ~20s grace
          clearInterval(pollRef.current);
          if (await recover(uploaded)) { setBusy(false); return; }
          setBusy(false);
          setLostJob({
            reason: "The server restarted while this drawing was being processed — run it again.",
          });
          return toast.error("Server restarted — run again.");
        }
        clearInterval(pollRef.current);
        setBusy(false);
        setLostJob({ reason: e?.response?.data?.detail || "Lost contact with the job." });
        toast.error(e?.response?.data?.detail || "Lost contact with the job.");
      }
    }, POLL_MS);
  }, [adopt, recover, loadRecents, navigate]);

  const run = async () => {
    if (!file) return toast.error("Choose a drawing first.");
    setBusy(true);
    setJob(null);
    setSelected(null);
    setEdits({});
    setLostJob(null);
    setRecovered(null);
    const uploaded = file;
    const fd = new FormData();
    fd.append("file", uploaded);
    fd.append("engine", engine);
    try {
      const { data } = await api.post("/kc/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob({ ...data, status: "queued" });
      poll(data.job_id, uploaded);
    } catch (e) {
      setBusy(false);
      setLostJob({ reason: e?.response?.data?.detail || "Could not start the job." });
      toast.error(e?.response?.data?.detail || "Could not start the job.");
    }
  };

  const saveCorrections = async () => {
    const payload = Object.entries(edits)
      .filter(([, v]) => v.corrected?.trim() && v.corrected.trim() !== v.predicted)
      .map(([index, v]) => ({
        index: Number(index),
        predicted: v.predicted,
        corrected: v.corrected.trim(),
        crop_url: v.crop_url,
      }));
    if (!payload.length) return toast.error("Nothing edited yet.");
    setSavingEdits(true);
    try {
      const { data } = await api.post(`/kc/jobs/${job.job_id}/corrections`, { corrections: payload });
      toast.success(`Saved ${data.saved} correction(s) — ${data.total} in the training set.`);
      setEdits({});
      const { data: fresh } = await api.get(`/kc/jobs/${job.job_id}`);
      setJob(fresh);
      setStatus((s) => (s ? { ...s, corrections_count: data.total } : s));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save corrections.");
    } finally {
      setSavingEdits(false);
    }
  };

  // Downloads go through `api` so the Authorization fallback applies — a bare anchor
  // href would only carry the session cookie.
  const download = async (path, name) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
    }
  };

  const editCount = useMemo(
    () => Object.values(edits).filter((v) => v.corrected?.trim() && v.corrected.trim() !== v.predicted).length,
    [edits]
  );

  // Artifact URLs come back absolute under /api; `api` already has that as its baseURL.
  const apiPath = (u) => (u ? u.replace(/^\/api/, "") : null);

  // Crop thumbnails are fetched once per finished job into blob URLs — a hook per row
  // is not an option, and each fetch needs the auth header.
  const [cropBlobs, setCropBlobs] = useState({});
  useEffect(() => {
    if (job?.status !== "done") return undefined;
    const urls = new Set();
    (job.rows || []).forEach((r) => r.crop_url && urls.add(r.crop_url));
    (job.crops || []).forEach((c) => c && urls.add(c));
    if (!urls.size) return undefined;
    let cancelled = false;
    const revokers = [];
    Promise.all(
      [...urls].map(async (u) => {
        const res = await fetchAuthedImage(apiPath(u));
        if (!res) return null;
        if (cancelled) { res.revoke(); return null; }
        revokers.push(res.revoke);
        return [u, res.url];
      })
    ).then((pairs) => {
      if (!cancelled) setCropBlobs(Object.fromEntries(pairs.filter(Boolean)));
    });
    return () => { cancelled = true; revokers.forEach((fn) => fn()); setCropBlobs({}); };
  }, [job?.job_id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const ballooned = useAuthedImage(
    job?.status === "done" ? apiPath(job.ballooned_png_url) : null, [job?.job_id, job?.status]
  );
  const overlay = useAuthedImage(
    job?.status === "done" ? apiPath(job.overlay_png_url) : null, [job?.job_id, job?.status]
  );

  return (
    <div className="space-y-4 p-4" data-testid="kc-test-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Key Characteristics — test bench</h1>
          <p className="text-sm text-muted-foreground">
            Upload a drawing and run it. The engine is picked from the file itself — a CAD PDF goes
            to the v3 text-layer path, a scan or image to Balloon Studio.
          </p>
        </div>
        {status && (
          <span className="text-xs text-muted-foreground">
            {status.corrections_count} correction(s) collected
            {status.corrections_count > 0 && (
              <button className="ml-2 underline" onClick={() => download("/kc/corrections/export", "kc_corrections.jsonl")}>
                export
              </button>
            )}
          </span>
        )}
      </div>

      {/* KC Recents — open any finished extraction in the full KcViewer result screen. */}
      {kcRecents.length > 0 && (
        <div className="rounded-lg border p-3" data-testid="kc-recents">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">KC Recents</div>
          <ul className="divide-y">
            {kcRecents.map((d) => (
              <li key={d.id} className="flex items-center gap-2 py-1.5 text-sm" data-testid={`kc-recent-${d.id}`}>
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{d.name}</span>
                {d.engine_label && <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">{d.engine_label}</span>}
                <button
                  onClick={() => navigate(`/kc-test/${d.id}`)}
                  data-testid={`kc-recent-open-${d.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 px-2.5 py-1 text-violet-700 hover:bg-violet-100"
                >
                  <Maximize2 className="h-3.5 w-3.5" /> Open
                </button>
                <button
                  onClick={() => deleteRecent(d)}
                  disabled={deletingId === d.id}
                  title="Remove this run from KC Recents (no undo in the app)"
                  aria-label={`Delete ${d.name}`}
                  data-testid={`kc-recent-delete-${d.id}`}
                  className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-2 py-1 text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-50"
                >
                  {deletingId === d.id
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* An engine that cannot run — say exactly why rather than silently falling back. */}
      {status && !v3Ready && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>v3 engine unavailable.</strong> {status.v3.detail}
            <div className="mt-1 text-xs">
              Expected at <code className="rounded bg-amber-100 px-1">{status.v3.path}</code>. Legacy still runs.
            </div>
          </div>
        </div>
      )}
      {status && !bsReady && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>Balloon Studio (scan engine) unavailable.</strong> {status.bs.detail}
            <div className="mt-1 text-xs">
              Expected in <code className="rounded bg-amber-100 px-1">{status.bs.vendor_dir}</code>.
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="rounded-lg border p-4">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          {SHOW_ENGINE_PICKER && (
            <div className="inline-flex overflow-hidden rounded-md border" role="group" aria-label="Engine">
              {ENGINE_ORDER.map((e) => (
                <button
                  key={e}
                  disabled={!engineReady(e)}
                  onClick={() => setEngine(e)}
                  title={(status?.engines || []).find((x) => x.value === e)?.label || e}
                  className={`px-3 py-1.5 text-sm ${engine === e ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"} ${!engineReady(e) ? "cursor-not-allowed opacity-40" : ""}`}
                  data-testid={`kc-engine-${e}`}
                >
                  {ENGINE_SHORT[e]}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={run}
            disabled={busy || !file}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            data-testid="kc-run"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {busy ? "Running…" : "Run extraction"}
          </button>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
          onClick={() => fileRef.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-sm ${dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"}`}
          data-testid="kc-dropzone"
        >
          <input
            ref={fileRef} type="file" accept={accept} className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          {file ? (
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="font-medium">{file.name}</span>
              <span className="text-muted-foreground">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
              <button
                onClick={(e) => { e.stopPropagation(); setFile(null); setJob(null); }}
                className="rounded p-0.5 hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <UploadCloud className="h-6 w-6 text-muted-foreground" />
              <span>
                Drop a drawing here, or click to choose — PDF, PNG, JPG, TIF or BMP
                {status?.max_upload_mb ? ` (up to ${status.max_upload_mb} MB)` : ""}
              </span>
            </>
          )}
        </div>

        {busy && (
          <div className="mt-3 space-y-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
            </div>
            <p className="text-xs text-muted-foreground">
              {job?.status === "queued"
                ? (job?.queue_position > 1 ? `Queued — ${ordinal(job.queue_position)} in line` : "Queued — next up")
                : "Extracting"}
              {job?.status === "queued"
                ? (job?.queued_seconds ? ` · waited ${Math.round(job.queued_seconds)}s` : "")
                : (job?.elapsed_seconds ? ` — ${job.elapsed_seconds}s elapsed` : "")}
            </p>
            {job?.stage && (
              <p className="truncate font-mono text-xs text-muted-foreground" title={job.stage}>
                {job.stage.replace(/^\[debug\]\s*/, "")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* The run produced nothing this page could show, and no earlier result for this
          upload could be found either. Say so plainly and offer the one useful action. */}
      {lostJob && job?.status !== "done" && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
          data-testid="kc-lost-job"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="font-medium">{lostJob.reason}</span>
          <button
            className="ml-auto inline-flex items-center gap-1 rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            onClick={run}
            disabled={busy || !file}
            data-testid="kc-retry"
          >
            <RefreshCw className="h-3 w-3" /> Retry
          </button>
        </div>
      )}

      {/* The job id went away mid-run but the finished result was found again. */}
      {recovered && job?.status === "done" && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
          data-testid="kc-recovered"
        >
          <Check className="h-4 w-4 shrink-0" />
          <span>
            This run was interrupted, but its finished result was recovered — nothing was lost.
          </span>
          {recovered.doc_id && (
            <a className="ml-auto underline" href={`/ballooning/${recovered.doc_id}`}>
              Open in Ballooning
            </a>
          )}
        </div>
      )}

      {job?.status === "failed" && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" /> Extraction failed on this drawing
          </div>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs">{job.error}</pre>
        </div>
      )}

      {job?.status === "done" && (
        <>
          {/* Engine warnings (e.g. a 105-DPI scan) — the result is still shown, but the
              reason it may be thin is stated up front. */}
          {warnings.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" /> Check the source file
              </div>
              <ul className="mt-1 list-disc pl-6">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Summary + downloads */}
          <div className="flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm">
            <span><strong>{rows.length}</strong> rows</span>
            <span className="text-muted-foreground">·</span>
            <span><strong>{review.length}</strong> need review</span>
            <span className="text-muted-foreground">·</span>
            <span>{job.elapsed_seconds}s</span>
            <span className="text-muted-foreground">·</span>
            {/* Which engine actually ran. "Auto" picks per file, so showing the request
                alone left it ambiguous which path produced the table. */}
            <span
              className="rounded border bg-muted px-2 py-0.5 text-xs"
              title={job.engine_why || ""}
              data-testid="kc-engine-used"
            >
              ran: <strong>{job.engine_label || ENGINE_SHORT[job.engine_used] || job.engine_used || job.engine}</strong>
              {job.engine !== job.engine_used ? ` (asked: ${ENGINE_SHORT[job.engine] || job.engine})` : ""}
            </span>
            {job.text_source && (
              <span className="text-xs text-muted-foreground">
                text: {job.text_source}{job.scan_dpi ? ` · ~${job.scan_dpi} dpi` : ""}
              </span>
            )}
            <div className="ml-auto flex gap-2">
              {job.result_doc_id && (
                <a
                  href={`/ballooning/${job.result_doc_id}`}
                  data-testid="kc-open-result-screen"
                  className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 px-3 py-1.5 text-violet-700 hover:bg-violet-100"
                >
                  <Maximize2 className="h-4 w-4" /> Open result screen
                </a>
              )}
              <button
                onClick={() => download(`/kc/jobs/${job.job_id}/export.xlsx`, "kc.xlsx")}
                className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 hover:bg-muted"
              >
                <Download className="h-4 w-4" /> Excel
              </button>
              <button
                onClick={() => download(`/kc/jobs/${job.job_id}/ballooned.pdf`, "ballooned.pdf")}
                disabled={!job.ballooned_png_url}
                className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 hover:bg-muted disabled:opacity-40"
              >
                <Download className="h-4 w-4" /> Ballooned PDF
              </button>
              <button
                onClick={() => setShowLog((s) => !s)}
                className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 hover:bg-muted"
              >
                <Terminal className="h-4 w-4" /> Log
              </button>
            </div>
          </div>

          {showLog && (
            <pre className="max-h-72 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
              {job.debug_log || "(no debug output)"}
            </pre>
          )}

          {/* Drawing */}
          <div className="rounded-lg border p-3">
            <div className="mb-2 inline-flex overflow-hidden rounded-md border text-sm">
              {["ballooned", "overlay"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-1 ${tab === t ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                >
                  {t === "ballooned" ? "Ballooned" : "CV overlay"}
                </button>
              ))}
            </div>
            <ZoomableImage
              src={tab === "ballooned" ? ballooned : overlay}
              alt={tab}
              highlight={selected}
            />
          </div>

          {/* Rows */}
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Crop</th>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={`px-2 py-2 text-left font-medium ${c.w || ""}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.No}
                    onClick={() => setSelected(r.No)}
                    className={`cursor-pointer border-t hover:bg-muted/40 ${selected === r.No ? "bg-primary/10" : ""}`}
                    data-testid={`kc-row-${r.No}`}
                  >
                    <td className="px-2 py-1">
                      {cropBlobs[r.crop_url] ? (
                        <img src={cropBlobs[r.crop_url]} alt={`crop ${r.No}`} className="h-8 rounded border bg-white object-contain" />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    {COLUMNS.map((c) => {
                      // v3 names this column Feature, Balloon Studio names it Type —
                      // fall back so one table renders either engine.
                      const raw = r[c.key] !== undefined && r[c.key] !== null && r[c.key] !== ""
                        ? r[c.key]
                        : (c.fallback ? r[c.fallback] : r[c.key]);
                      return (
                        <td key={c.key} className={`px-2 py-1 ${c.mono ? "font-mono whitespace-nowrap" : ""} ${r.corrected && c.key === "Value" ? "text-emerald-700" : ""}`}>
                          {c.basic ? (
                            raw
                              ? <span className="rounded bg-indigo-200 px-1 text-[10px] font-semibold">BASIC</span>
                              : <span className="text-xs text-muted-foreground">—</span>
                          ) : c.verbatim ? (
                            // never trim or reformat — '143 0/-1' must stay '143 0/-1'
                            raw === null || raw === undefined || raw === "" ? "—" : String(raw)
                          ) : fmt(raw)}
                          {c.key === "Value" && r.Reference && (
                            <span className="ml-1 rounded bg-slate-200 px-1 text-[10px]">ref</span>
                          )}
                          {c.key === "Value" && r.Check && (
                            <AlertTriangle className="ml-1 inline h-3 w-3 text-amber-600" title={r.Check} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={COLUMNS.length + 1} className="px-3 py-6 text-center text-muted-foreground">
                    This run returned no rows.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Needs review */}
          {review.length > 0 && (
            <div className="rounded-lg border border-amber-300">
              <div className="flex items-center justify-between gap-2 border-b border-amber-300 bg-amber-50 px-3 py-2">
                <span className="flex items-center gap-2 text-sm font-medium text-amber-900">
                  <AlertTriangle className="h-4 w-4" /> Needs review — {review.length}
                </span>
                <button
                  onClick={saveCorrections}
                  disabled={savingEdits || !editCount}
                  className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1 text-sm text-white disabled:opacity-40"
                  data-testid="kc-save-corrections"
                >
                  {savingEdits ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save {editCount || ""} correction{editCount === 1 ? "" : "s"}
                </button>
              </div>
              <div className="divide-y">
                {review.map((rv) => {
                  // Each review row carries its own crop URL. Looking one up by list
                  // position in the accepted rows showed the wrong thumbnail.
                  const cropUrl = rv.crop_url;
                  const cur = edits[rv.index]?.corrected ?? rv.value ?? "";
                  return (
                    <div key={rv.index} className="flex flex-wrap items-center gap-3 p-3">
                      {cropBlobs[cropUrl] ? (
                        <img src={cropBlobs[cropUrl]} alt="crop" className="h-10 rounded border bg-white object-contain" />
                      ) : (
                        <div className="flex h-10 w-20 items-center justify-center rounded border text-xs text-muted-foreground">no crop</div>
                      )}
                      <div className="text-sm">
                        <div className="text-xs text-muted-foreground">predicted</div>
                        <code className="font-mono">{rv.value || "—"}</code>
                      </div>
                      <input
                        value={cur}
                        onChange={(e) =>
                          setEdits((s) => ({
                            ...s,
                            [rv.index]: { predicted: rv.value || "", corrected: e.target.value, crop_url: cropUrl },
                          }))
                        }
                        className="w-48 rounded-md border px-2 py-1 font-mono text-sm"
                        placeholder="corrected value"
                        data-testid={`kc-review-input-${rv.index}`}
                      />
                      {cur && cur !== rv.value && <Check className="h-4 w-4 text-emerald-600" />}
                      {/* the engine's own `check` text, shown in full — it says WHY this
                          row needs a human, which a confidence number alone does not. */}
                      <span className="ml-auto max-w-[36rem] text-right text-xs text-amber-800">
                        {rv.confidence != null && (
                          <span className="mr-2 text-muted-foreground">conf {rv.confidence}</span>
                        )}
                        {rv.reason}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Side-by-side comparison, once more than one engine has run this drawing */}
          {Object.keys(compare).length > 1 && (
            <div className="rounded-lg border p-3">
              <h2 className="mb-2 text-sm font-medium">Engine comparison — same drawing</h2>
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr><th className="px-2 py-1 text-left">Engine</th><th className="px-2 py-1 text-left">Rows</th>
                    <th className="px-2 py-1 text-left">Needs review</th><th className="px-2 py-1 text-left">Seconds</th></tr>
                </thead>
                <tbody>
                  {Object.keys(compare).sort().map((e) => (
                    <tr key={e} className="border-t">
                      <td className="px-2 py-1">{compare[e].engine_label || ENGINE_SHORT[e] || e}</td>
                      <td className="px-2 py-1">{(compare[e].rows || []).length}</td>
                      <td className="px-2 py-1">{(compare[e].review || []).length}</td>
                      <td className="px-2 py-1">{compare[e].elapsed_seconds}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-muted-foreground">
                Run the same file under more than one engine to populate this. Row counts alone are not
                accuracy — compare the values.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
